import { useMemo, useEffect } from "react";
import { useWindowDimensions } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { View } from "@src/components";
import { DataStateView } from "@src/components/screen-state";
import { WeekCalendar, ClipVideoPlayer, ClipsTimeline, ActivityHeatmap } from "@src/features/clips";
import { Stack, useLocalSearchParams } from "expo-router";
import { useUnistyles } from "react-native-unistyles";
import * as ScreenOrientation from "expo-screen-orientation";
import { HeaderButton } from "@react-navigation/elements";
import { useHeaderBackButton } from "@src/hooks/useHeaderBackButton";
import Animated, { FadeInUp, FadeOutDown } from "react-native-reanimated";
import { Icon } from "@src/components/Icon";
import { useCameraDetailsData } from "@src/features/cameras/hooks/useCameraDetailsData";
import { getTimezoneFromAlarmDetail } from "@src/features/alarms/utils/alarm-utils";
import { useClipsLogic } from "@src/features/clips/hooks/useClipsLogic";

export default function ClipsScreen() {
  const { theme } = useUnistyles();
  const { height: screenHeight, width: screenWidth } = useWindowDimensions();
  const insets = useSafeAreaInsets();
  const { siteId, cameraKey } = useLocalSearchParams<{ siteId: string; cameraKey: string }>();
  const { headerLeft } = useHeaderBackButton("/(tabs)/overview");
  const { alarmDetail } = useCameraDetailsData();
  const timezone = useMemo(
    () => getTimezoneFromAlarmDetail(alarmDetail) || Intl.DateTimeFormat().resolvedOptions().timeZone,
    [alarmDetail],
  );

  useEffect(() => {
    const lockOrientation = async () => {
      try {
        await ScreenOrientation.lockAsync(ScreenOrientation.OrientationLock.PORTRAIT_UP);
      } catch (error) {
        console.warn("Failed to lock orientation:", error);
      }
    };

    lockOrientation();

    return () => {
      ScreenOrientation.lockAsync(ScreenOrientation.OrientationLock.PORTRAIT_UP);
    };
  }, []);

  const {
    selectedDate,
    selectedHour,
    today,
    dateDirection,
    handleDateSelect,
    handleHourSelect,

    hourBuckets,
    isLoadingHourlyClipCounts,

    clipsData,
    isLoadingClips,
    isFetchingClips,
    clipsError,
    refetchAll,

    selectedClip,
    selectedClipId,
    selectedClipIndex,
    totalClipsInHour,
    handleClipSelect,
    handlePrevious,
    handleNext,
    hasPrevious,
    hasNext,

    hasMore,
    handleLoadMore,

    isDownloading,
    handleDownload,
  } = useClipsLogic({
    siteId: siteId,
    cameraKey: cameraKey,
    timezone,
  });

  const videoPlayerProps = useMemo(
    () => ({
      clip: selectedClip,
      siteId,
      cameraKey,
      isLoading: isLoadingClips,
      onPrevious: handlePrevious,
      onNext: handleNext,
      hasPrevious,
      hasNext,
      screenHeight,
      screenWidth,
      selectedHour,
    }),
    [
      selectedClip,
      siteId,
      cameraKey,
      handlePrevious,
      handleNext,
      hasPrevious,
      hasNext,
      screenHeight,
      screenWidth,
      selectedHour,
      isLoadingClips,
    ],
  );

  const headerOptions = useMemo(
    () => ({
      headerShown: true,
      headerTitle: "Recordings",
      headerBackButtonDisplayMode: "minimal" as const,
      headerStyle: {
        backgroundColor: theme.colors.background,
      },
      headerTintColor: theme.colors.text,
      headerShadowVisible: false,
      headerLeft,
      headerRight: () =>
        selectedClip ?
          <HeaderButton onPress={handleDownload} disabled={isDownloading}>
            <Icon name="download" size={24} color={theme.colors.text} />
          </HeaderButton>
        : null,
    }),
    [theme.colors.background, theme.colors.text, handleDownload, selectedClip, isDownloading, headerLeft],
  );

  return (
    <>
      <Stack.Screen options={headerOptions} />
      <View flex={1} bg="background" style={{ paddingBottom: insets.bottom }}>
        <View px="2xl" mb="4xl">
          <View px="3xl" py="md" bg="surfaceCard" rounded={theme.spacing.xl} borderWidth={1} borderColor="borderCard">
            <WeekCalendar selectedDate={selectedDate} onDateSelect={handleDateSelect} today={today} />
            <View height={1} bg="borderCard" my="xs" />
            <ActivityHeatmap
              selectedDate={selectedDate}
              selectedHour={selectedHour}
              onHourSelect={handleHourSelect}
              hourBuckets={hourBuckets}
              isLoading={isLoadingHourlyClipCounts}
              dateDirection={dateDirection}
              timezone={timezone}
            />
          </View>
        </View>
        <DataStateView
          data={clipsData}
          isLoading={isLoadingClips}
          isFetching={isFetchingClips}
          error={clipsError}
          refetch={refetchAll}
          isEmpty={(clips) => clips.length === 0}
          loadingMessage="Loading clips..."
          errorTitle="Failed to load clips"
          errorMessage="Please check your connection and try again."
          emptyState={{
            icon: "videoOutline",
            title: "No clips found",
            message: "There are no clips available for this hour.",
          }}
        >
          {(clips) => (
            <Animated.View
              entering={FadeInUp}
              exiting={FadeOutDown}
              key={selectedDate.toISOString() + selectedHour.toString()}
            >
              <View justifyContent="center" px="lg" mb="lg">
                <ClipVideoPlayer
                  {...videoPlayerProps}
                  clipIndex={selectedClipIndex + 1}
                  totalClips={totalClipsInHour}
                />
              </View>
              <ClipsTimeline
                clips={clips}
                selectedClipId={selectedClipId}
                onClipSelect={handleClipSelect}
                hasNextPage={hasMore}
                isFetchingNextPage={false}
                onLoadMore={handleLoadMore}
                timezone={timezone}
              />
            </Animated.View>
          )}
        </DataStateView>
      </View>
    </>
  );
}
